import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { Bot, MessageSquareText, Play, Trash2, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BsLightningCharge } from "react-icons/bs";
import { toast } from "sonner";
import { BrandDisplayPeriodControl } from "@/components/brand-display-period-control";
import { InlineEditableText } from "@/components/inline-editable-text";
import { LlmPicker } from "@/components/llm-picker";
import { DashboardContentZone, DashboardControlGroup, DashboardControlRow } from "@/components/patterns/content-zone";
import { DashboardPageActions, DashboardPageHeader, DashboardPageShell } from "@/components/patterns/headers";
import { PromptsTable } from "@/components/prompts-table/prompts-table";
import { SearchActivityPill } from "@/components/search-activity-pill";
import { SearchMetadataPill } from "@/components/search-metadata-pill";
import { SearchScheduler } from "@/components/search-schedule/search-scheduler";
import {
      SearchSpecificationMentionChart,
      SearchSpecificationRankingChart,
      SearchSpecificationSentimentChart,
      SearchSpecificationVisibilityChart,
} from "@/components/search-specification-charts";
import { SearchSpecificationFailureBanner } from "@/components/search-specification-failure-banner";
import { PromptTypeTooltip } from "@/components/tooltips/prompt-type";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { LoadingButton } from "@/components/ui/custom/loading-button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchSpecificationActivity } from "@/hooks/use-search-specification-activity";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { shouldInvalidateSearchChartsForActivity } from "@/lib/search-activity";
import { trpcClient, useTRPC } from "@/lib/trpc";
import { useBrandStore } from "@/state/store/brand";
import { useUserPreferencesStore } from "@/state/store/userPreferences";

type PromptSortField =
      | "text"
      | "promptType"
      | "status"
      | "createdAt"
      | "responseCount"
      | "visibilityScore"
      | "avgSentiment"
      | "mentionRate"
      | "averageRanking";

export const Route = createFileRoute("/_dashboard/$brand/searches/$search/")({
      component: RouteComponent,
});

export function RouteComponent() {
      const trpc = useTRPC();
      const navigate = useNavigate();
      const { search, brand: brandSlug } = useParams({
            from: "/_dashboard/$brand/searches/$search/",
      });
      const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
      const { search: searchSpecificationId } = Route.useParams();
      const queryClient = useQueryClient();

      const selectedBrandId = useBrandStore((state) => state.selectedBrandId);
      const defaultBrandDashboardPeriod = useUserPreferencesStore((state) => state.defaultBrandDashboardPeriod);
      const displayPeriodQuery = useMemo(
            () => getDisplayPeriodQuery(defaultBrandDashboardPeriod),
            [defaultBrandDashboardPeriod],
      );
      const { activity, latestEvent, setOptimisticActivity } = useSearchSpecificationActivity(searchSpecificationId);

      const { data: searchSpecification } = useQuery(
            trpc.searchSpecifications.getSearchSpecification.queryOptions({
                  id: search!,
            }),
      );

      const { data: persona } = useQuery(
            trpc.personas.getPersona.queryOptions(
                  { id: searchSpecification?.personaId ?? "" },
                  { enabled: !!searchSpecification?.personaId },
            ),
      );

      const { data: executionCount } = useQuery(
            trpc.searchSpecifications.getSearchSpecificationSearchExecutionsCount.queryOptions({
                  id: searchSpecificationId!,
            }),
      );

      const { data: promptCount } = useQuery(
            trpc.searchSpecifications.getSearchSpecificationPromptsCount.queryOptions({
                  id: searchSpecificationId!,
            }),
      );

      const { data: metrics } = useQuery(
            trpc.searchSpecifications.getSearchSpecificationMetrics.queryOptions(
                  {
                        searchSpecificationId: searchSpecificationId!,
                        brandId: selectedBrandId ?? "",
                        ...displayPeriodQuery,
                  },
                  {
                        enabled: !!selectedBrandId,
                  },
            ),
      );

      const llmCount = searchSpecification?.llmIds?.filter((llmId) => llmId !== null).length || 0;
      const totalResponses = metrics?.responseCount ?? 0;

      const runSearchMutation = useMutation(
            trpc.searches.startSearch.mutationOptions({
                  onSuccess: (searchActivity) => {
                        toast.success("Search started");
                        setOptimisticActivity(searchActivity);
                        const currentBrandActivity = queryClient.getQueryData(
                              trpc.brands.getBrandSearchActivity.queryKey({
                                    brandId: searchActivity.brandId,
                              }),
                        ) as
                              | (typeof searchActivity & {
                                      activeExecutionCount: number;
                                })
                              | null
                              | undefined;
                        queryClient.setQueryData(
                              trpc.brands.getBrandSearchActivity.queryKey({
                                    brandId: searchActivity.brandId,
                              }),
                              {
                                    ...searchActivity,
                                    activeExecutionCount: Math.max(currentBrandActivity?.activeExecutionCount ?? 0, 1),
                              },
                        );
                        queryClient.setQueriesData(
                              {
                                    queryKey: trpc.searchExecutions.getSearchExecutionsByBrandId.queryKey({
                                          brandId: searchActivity.brandId,
                                    }),
                              },
                              (previous) => {
                                    if (!previous || typeof previous !== "object" || !("items" in previous)) {
                                          return previous;
                                    }

                                    const paginated = previous as { items: Array<typeof searchActivity> };
                                    const existingIndex = paginated.items.findIndex(
                                          (item) => item.id === searchActivity.id,
                                    );
                                    const nextItems = [...paginated.items];

                                    if (existingIndex >= 0) {
                                          nextItems[existingIndex] = {
                                                ...nextItems[existingIndex],
                                                ...searchActivity,
                                          };
                                    } else {
                                          nextItems.unshift(searchActivity);
                                    }

                                    return {
                                          ...previous,
                                          items: nextItems,
                                    };
                              },
                        );
                        queryClient.invalidateQueries({
                              queryKey: trpc.searchSpecifications.getSearchSpecificationSearchExecutionsCount.queryKey({
                                    id: searchSpecificationId!,
                              }),
                        });
                  },
                  onError: (error) => {
                        queryClient.invalidateQueries({
                              queryKey: trpc.searchSpecifications.getSearchSpecificationSearchExecutionsCount.queryKey({
                                    id: searchSpecificationId!,
                              }),
                        });
                        queryClient.invalidateQueries({
                              queryKey: trpc.searchExecutions.getSearchExecutions.queryKey({
                                    searchSpecificationId: search!,
                              }),
                        });
                        queryClient.invalidateQueries({
                              queryKey: trpc.searchExecutions.getLatestSearchSpecificationSearchExecution.queryKey({
                                    searchSpecificationId: search!,
                              }),
                        });
                        toast.error(error.message || "Failed to start search");
                  },
            }),
      );

      const updateSearchSpecificationMutation = useMutation(
            trpc.searchSpecifications.updateSearchSpecification.mutationOptions({
                  onSuccess: () => {
                        queryClient.invalidateQueries({
                              queryKey: trpc.searchSpecifications.getSearchSpecification.queryKey({
                                    id: searchSpecificationId!,
                              }),
                        });
                  },
            }),
      );

      const deleteSearchSpecificationMutation = useMutation(
            trpc.searchSpecifications.deleteSearchSpecification.mutationOptions({
                  onSuccess: () => {
                        toast.success("Search archived. You can restore it from Activity History.");
                        setIsDeleteDialogOpen(false);
                        navigate({
                              to: "/$brand/searches",
                              params: { brand: brandSlug },
                        });
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to archive search");
                  },
            }),
      );

      const { data: executions } = useQuery(
            trpc.searchExecutions.getSearchExecutions.queryOptions({
                  searchSpecificationId: search!,
            }),
      );

      const hasExecutions = executions && executions.items.length > 0;
      const isSearchButtonLoading = runSearchMutation.isPending || Boolean(activity?.isActive);

      // Helper to invalidate all chart data queries
      const invalidateChartData = useCallback(() => {
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getSearchSpecificationVisibilityChartData.queryKey({
                        searchSpecificationId: search!,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getSearchSpecificationMentionChartData.queryKey({
                        searchSpecificationId: search!,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getSearchSpecificationSentimentChartData.queryKey({
                        searchSpecificationId: search!,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getSearchSpecificationRankingChartData.queryKey({
                        searchSpecificationId: search!,
                        ...displayPeriodQuery,
                  }),
            });
      }, [
            displayPeriodQuery,
            queryClient,
            search,
            trpc.charts.getSearchSpecificationMentionChartData,
            trpc.charts.getSearchSpecificationRankingChartData,
            trpc.charts.getSearchSpecificationSentimentChartData,
            trpc.charts.getSearchSpecificationVisibilityChartData,
      ]);

      useEffect(() => {
            if (!latestEvent) {
                  return;
            }

            if (shouldInvalidateSearchChartsForActivity(latestEvent)) {
                  invalidateChartData();
            }

            if (!latestEvent.isActive) {
                  queryClient.invalidateQueries({
                        queryKey: trpc.searchSpecifications.getSearchSpecificationSearchExecutionsCount.queryKey({
                              id: searchSpecificationId!,
                        }),
                  });
                  queryClient.invalidateQueries({
                        queryKey: trpc.searchExecutions.getSearchExecutions.queryKey({
                              searchSpecificationId: search!,
                        }),
                  });
            }
      }, [
            latestEvent,
            queryClient,
            search,
            searchSpecificationId,
            invalidateChartData,
            trpc.charts.getSearchSpecificationMentionChartData,
            trpc.charts.getSearchSpecificationRankingChartData,
            trpc.charts.getSearchSpecificationSentimentChartData,
            trpc.charts.getSearchSpecificationVisibilityChartData,
            trpc.searchExecutions.getSearchExecutions,
            trpc.searchSpecifications.getSearchSpecificationSearchExecutionsCount,
      ]);

      const handleUpdateName = async (newName: string) => {
            if (!searchSpecification?.id) return;
            await updateSearchSpecificationMutation.mutateAsync({
                  id: searchSpecification.id,
                  name: newName,
            });
      };

      const handleUpdateDescription = async (newDescription: string) => {
            if (!searchSpecification?.id) return;
            await updateSearchSpecificationMutation.mutateAsync({
                  id: searchSpecification.id,
                  description: newDescription,
            });
      };

      const promptsCallback = ({
            skip,
            take,
            sortBy,
            sortOrder,
            textFilter,
      }: {
            skip: number;
            take: number;
            sortBy?: PromptSortField;
            sortOrder?: "asc" | "desc";
            textFilter?: string;
      }) =>
            trpcClient.prompts.getSearchSpecificationPrompts.query({
                  searchSpecificationId: search!,
                  skip,
                  take,
                  sortBy,
                  sortOrder,
                  textFilter,
            });

      return (
            <DashboardPageShell contentClassName="flex flex-col gap-6">
                  <DashboardPageHeader
                        size="detail"
                        meta={
                              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                    <div className="flex flex-wrap items-center gap-2">
                                          <SearchMetadataPill
                                                icon={User}
                                                label="Persona"
                                                value={persona?.name ?? "General Public"}
                                                tone={persona ? "info" : "neutral"}
                                                truncateValue={!!persona}
                                          />
                                          <SearchScheduler searchSpecificationId={searchSpecificationId!} />
                                          <SearchActivityPill activity={activity} />
                                    </div>
                                    {searchSpecification?.createdAt && (
                                          <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                                                {searchSpecification?.createdAt && (
                                                      <span>
                                                            {new Date(searchSpecification.createdAt).toLocaleDateString(
                                                                  "en-US",
                                                                  {
                                                                        year: "numeric",
                                                                        month: "short",
                                                                        day: "numeric",
                                                                  },
                                                            )}
                                                      </span>
                                                )}
                                          </div>
                                    )}
                              </div>
                        }
                        titleContent={
                              <InlineEditableText
                                    value={searchSpecification?.name || ""}
                                    onSave={handleUpdateName}
                                    placeholder="Enter search name..."
                                    textClassName="text-2xl font-semibold tracking-tight text-brand-ink sm:text-3xl"
                                    editClassName="text-2xl font-semibold tracking-tight text-brand-ink sm:text-3xl"
                                    required
                              />
                        }
                        descriptionContent={
                              <InlineEditableText
                                    value={searchSpecification?.description || ""}
                                    onSave={handleUpdateDescription}
                                    placeholder="Enter search description..."
                                    textClassName="text-sm text-muted-foreground"
                                    multiline
                              />
                        }
                        aside={
                              <DashboardPageActions>
                                    <LoadingButton
                                          loading={isSearchButtonLoading}
                                          disabled={isSearchButtonLoading}
                                          onClick={() => {
                                                runSearchMutation.mutate({
                                                      searchSpecificationId: search!,
                                                });
                                          }}
                                    >
                                          <Play size={16} />
                                          {activity?.isActive ? "Search Running" : "Run Search"}
                                    </LoadingButton>
                                    <Button
                                          variant="quietDestructive"
                                          className="gap-2"
                                          onClick={() => setIsDeleteDialogOpen(true)}
                                    >
                                          <Trash2 size={16} />
                                          Delete
                                    </Button>
                              </DashboardPageActions>
                        }
                        below={
                              <div className="flex flex-wrap items-center gap-4 text-muted-foreground sm:gap-6">
                                    <span className="flex items-center gap-1.5 text-sm">
                                          <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="h-3.5 w-3.5 shrink-0"
                                                aria-hidden="true"
                                          >
                                                <path d="M15 5h6" />
                                                <path d="M15 12h6" />
                                                <path d="M3 19h18" />
                                                <path d="m3 12 3.553-7.724a.5.5 0 0 1 .894 0L11 12" />
                                                <path d="M3.92 10h6.16" />
                                          </svg>
                                          <span className="font-medium text-foreground">{promptCount ?? 0}</span>{" "}
                                          Prompts
                                    </span>
                                    <span className="flex items-center gap-1.5 text-sm">
                                          <Bot className="h-3.5 w-3.5 shrink-0" />
                                          <span className="font-medium text-foreground">{llmCount}</span> Models
                                    </span>
                                    <span className="flex items-center gap-1.5 text-sm">
                                          <BsLightningCharge className="h-3.5 w-3.5 shrink-0" />
                                          <span className="font-medium text-foreground">{executionCount ?? 0}</span>{" "}
                                          Runs
                                    </span>
                                    <span className="flex items-center gap-1.5 text-sm">
                                          <MessageSquareText className="h-3.5 w-3.5 shrink-0" />
                                          <span className="font-medium text-foreground">{totalResponses}</span>{" "}
                                          Responses
                                    </span>
                              </div>
                        }
                  />
                  <SearchSpecificationFailureBanner activity={activity} />
                  <div className="space-y-6">
                        {/* Chart Section */}
                        {hasExecutions && (
                              <Tabs defaultValue="visibility" className="w-full gap-4">
                                    <DashboardControlRow>
                                          <TabsList>
                                                <TabsTrigger value="visibility">Visibility</TabsTrigger>
                                                <TabsTrigger value="mentions">Mentions</TabsTrigger>
                                                <TabsTrigger value="sentiment">Sentiment</TabsTrigger>
                                                <TabsTrigger value="ranking">Ranking</TabsTrigger>
                                          </TabsList>
                                          <DashboardControlGroup className="md:justify-end">
                                                <BrandDisplayPeriodControl className="md:justify-end" />
                                          </DashboardControlGroup>
                                    </DashboardControlRow>
                                    <div className="relative min-h-[320px] sm:min-h-[400px]">
                                          <TabsContent
                                                value="visibility"
                                                forceMount
                                                className="absolute inset-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:opacity-0"
                                          >
                                                <SearchSpecificationVisibilityChart
                                                      searchSpecificationId={searchSpecificationId!}
                                                      period={defaultBrandDashboardPeriod}
                                                      activity={activity}
                                                />
                                          </TabsContent>
                                          <TabsContent
                                                value="mentions"
                                                forceMount
                                                className="absolute inset-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:opacity-0"
                                          >
                                                <SearchSpecificationMentionChart
                                                      searchSpecificationId={searchSpecificationId!}
                                                      period={defaultBrandDashboardPeriod}
                                                      activity={activity}
                                                />
                                          </TabsContent>
                                          <TabsContent
                                                value="sentiment"
                                                forceMount
                                                className="absolute inset-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:opacity-0"
                                          >
                                                <SearchSpecificationSentimentChart
                                                      searchSpecificationId={searchSpecificationId!}
                                                      period={defaultBrandDashboardPeriod}
                                                      activity={activity}
                                                />
                                          </TabsContent>
                                          <TabsContent
                                                value="ranking"
                                                forceMount
                                                className="absolute inset-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:opacity-0"
                                          >
                                                <SearchSpecificationRankingChart
                                                      searchSpecificationId={searchSpecificationId!}
                                                      period={defaultBrandDashboardPeriod}
                                                      activity={activity}
                                                />
                                          </TabsContent>
                                    </div>
                              </Tabs>
                        )}

                        <DashboardContentZone>
                              <h2 className="text-lg font-semibold">Select LLM Models</h2>
                              <LlmPicker searchSpecificationId={searchSpecificationId!} />
                        </DashboardContentZone>
                        <DashboardContentZone>
                              <h2 className="text-lg font-semibold">
                                    <span className="flex items-center gap-2">
                                          Prompts <PromptTypeTooltip />
                                    </span>
                              </h2>
                              <PromptsTable
                                    callbackFn={promptsCallback}
                                    searchSpecificationId={searchSpecificationId!}
                              />
                        </DashboardContentZone>
                  </div>
                  <ConfirmationDialog
                        open={isDeleteDialogOpen}
                        onOpenChange={setIsDeleteDialogOpen}
                        title="Archive Search"
                        description="This search configuration and its prompts will be archived from active use. Historical executions and responses stay recorded, and you can restore the archive from Activity History."
                        confirmText="Archive"
                        onConfirm={() =>
                              deleteSearchSpecificationMutation.mutate({
                                    id: searchSpecificationId!,
                              })
                        }
                        isPending={deleteSearchSpecificationMutation.isPending}
                        variant="destructive"
                  />
            </DashboardPageShell>
      );
}
